/**
 * CallOverlayShell
 *
 * Presentational shell for the visible in-app call overlay, shared by both the
 * Conversations call overlay (`AppCallOverlay`) and the Spaces voice-channel
 * overlay (`VoiceChannelOverlay`).
 *
 * - Renders the fixed `.call-overlay` element, resize handle, and the lazily
 *   loaded LiveKit room (`CallRoom`).
 * - Owns fullscreen + drag-to-resize behaviour and publishes the overlay height
 *   CSS var so the underlying content can pad itself below the overlay.
 * - Kept mounted (by its parent) while a session/tokens exist so the LiveKit
 *   audio connection persists across navigation; `visible` only toggles the
 *   video UI via `data-call-visible`.
 *
 * The troubleshoot ("Call Issues") modal is owned by each parent so it can also
 * be opened from other flows; this shell just forwards the in-overlay trigger.
 */

import { lazy, Suspense, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { StreamQualityCaps } from '@adieuu/shared';
import { useCallFullscreen } from '../../hooks/useCallFullscreen';
import { useCallOverlayResize } from '../../hooks/useCallOverlayResize';
import { setCallOverlayHeightCssVar } from '../../services/callOverlayPreferences';

// The LiveKit room (and its heavy bundle) loads only when a call is active.
const CallRoom = lazy(() => import('./CallRoom'));

export type CallOverlayVariant = 'conversation' | 'space';

export interface CallOverlayShellProps {
  variant: CallOverlayVariant;
  /** Whether the video UI should be shown (audio keeps flowing when false). */
  visible: boolean;
  /** Session phase, surfaced on the overlay as `data-phase` for styling. */
  phase?: string;
  serverUrl: string;
  token: string;
  callE2EEKey: Uint8Array | null;
  e2eeSupported: boolean;
  /** Whether E2E encryption was expected/attempted for this session. */
  e2eeExpected: boolean;
  streamQualityCaps: StreamQualityCaps | null;
  isDm: boolean;
  onConnected: () => void;
  onDisconnected: () => void;
  /** Opens the parent-owned "Call Issues" troubleshoot modal. */
  onTroubleshoot: () => void;
}

export function CallOverlayShell({
  variant,
  visible,
  phase,
  serverUrl,
  token,
  callE2EEKey,
  e2eeSupported,
  e2eeExpected,
  streamQualityCaps,
  isDm,
  onConnected,
  onDisconnected,
  onTroubleshoot,
}: CallOverlayShellProps) {
  const { t } = useTranslation();
  const overlayRef = useRef<HTMLDivElement>(null);

  const { isExpanded, toggle: toggleFullscreen } = useCallFullscreen(overlayRef);
  const { heightPx, committedHeightPx, resizeHandleProps } = useCallOverlayResize({
    disabled: isExpanded,
  });

  useEffect(() => {
    if (!visible || isExpanded) {
      setCallOverlayHeightCssVar(null);
      return;
    }
    setCallOverlayHeightCssVar(committedHeightPx);
  }, [visible, isExpanded, committedHeightPx]);

  useEffect(() => {
    return () => setCallOverlayHeightCssVar(null);
  }, []);

  const overlayClassName = useMemo(
    () =>
      [
        'call-overlay',
        variant === 'space' ? 'call-overlay--space' : '',
        isExpanded ? 'call-overlay--expanded' : '',
      ]
        .filter(Boolean)
        .join(' '),
    [variant, isExpanded],
  );

  return (
    <div
      ref={overlayRef}
      className={overlayClassName}
      style={isExpanded ? undefined : { height: `${heightPx}px` }}
      data-phase={phase}
      data-call-visible={visible}
      data-call-expanded={isExpanded}
    >
      <Suspense fallback={null}>
        <CallRoom
          serverUrl={serverUrl}
          token={token}
            callE2EEKey={callE2EEKey}
            e2eeSupported={e2eeSupported}
            e2eeExpected={e2eeExpected}
            streamQualityCaps={streamQualityCaps}
            isDm={isDm}
          isExpanded={isExpanded}
          onToggleFullscreen={() => void toggleFullscreen()}
          onTroubleshoot={onTroubleshoot}
          onConnected={onConnected}
          onDisconnected={onDisconnected}
        />
      </Suspense>
      {!isExpanded && (
        <hr
          className="call-overlay__resize-handle"
          tabIndex={0}
          aria-orientation="horizontal"
          aria-label={t('call.resizeOverlay')}
          {...resizeHandleProps}
        />
      )}
    </div>
  );
}
